import React, { useEffect, useState } from 'react';
import { func, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { path, pathOr } from 'ramda';

import config from '../../config';
import { ensureCurrentUser, ensureStripeCustomer, ensurePaymentMethodCard } from '../../util/data';
import { propTypes } from '../../util/types';
import {
  getCountryValue,
  isUsableStripePaymentMethod,
  shouldForceCartesBancairesResave,
} from '../../util/stripe';
import { savePaymentMethod, deletePaymentMethod } from '../../ducks/paymentMethods.duck';
import { handleCardSetup } from '../../ducks/stripe.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import {
  Divider, PrimaryButton,
  SavedCardDetails,
} from '../../components';
import { PaymentMethodsForm } from '../../forms';

import {
  createStripeSetupIntent,
  stripeCustomer,
} from '../../containers/AccountPaymentsPage/components/PaymentMethodsTab/PaymentMethodsTab.duck.js';
import css from './PaymentFormWithPaymentIntent.module.css';
import { injectIntl } from '../../util/reactIntl';
import { FormattedMessage } from 'react-intl';

const PaymentFormWithPaymentIntent = props => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardState, setCardState] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(false);
  const {
    currentUser,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    deletePaymentMethodInProgress,
    onCreateSetupIntent,
    onHandleCardSetup,
    onSavePaymentMethod,
    onDeletePaymentMethod,
    fetchStripeCustomer,
    onManageDisableScrolling,
    isLoading,
    onSubmit,
    confirmManualPaymentMultiBookingError,
    intl,
    stripePaymentMethodDetails,
  } = props;

  useEffect(() => {
    if (!window.Stripe) {
      throw new Error('Stripe must be loaded for PaymentMethodsForm');
    }

    if (config.stripe.publishableKey) {
      setStripe(window.Stripe(config.stripe.publishableKey));
    }
  }, []);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;
  const hasDefaultPaymentMethod =
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id;
  const hasUsablePaymentMethod = isUsableStripePaymentMethod(stripePaymentMethodDetails);
  const hasSetupIntent = !!path(['attributes', 'profile', 'privateData', 'clientSecretSetupIntent'], currentUser);
  const requiresCardResave = shouldForceCartesBancairesResave(stripePaymentMethodDetails);
  useEffect(() => {
    setHasValidPaymentMethod(
      hasDefaultPaymentMethod && hasUsablePaymentMethod && hasSetupIntent && !requiresCardResave
    );
  }, [hasSetupIntent, hasDefaultPaymentMethod, hasUsablePaymentMethod, requiresCardResave]);
  const getClientSecret = setupIntent => {
    return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
  };
  const piAuthenticationFailure = 'payment_intent_authentication_failure';
  const paymentErrorMessage =
    confirmManualPaymentMultiBookingError && confirmManualPaymentMultiBookingError.code === piAuthenticationFailure
      ? intl.formatMessage({ id: 'StripePaymentForm.confirmCardPaymentError' })
      : confirmManualPaymentMultiBookingError
        ? confirmManualPaymentMultiBookingError.message
        : intl.formatMessage({ id: 'StripePaymentForm.genericError' });
  const getPaymentParams = (currentUser, formValues) => {
    const { name, addressLine1, addressLine2, postal, state, city, country } = formValues;
    const addressMaybe =
      addressLine1 && postal
        ? {
          address: {
            city: city,
            country: country,
            line1: addressLine1,
            line2: addressLine2,
            postal_code: postal,
            state: state,
          },
        }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const paymentParams = {
      payment_method_data: {
        billing_details: billingDetails,
      },
    };

    return paymentParams;
  };

  const handleSubmitCard = params => {
    setIsSubmitting(true);
    const { stripe, card, formValues } = params;
    onSubmit({
      stripe,
      card,
      paymentParams: {
        payment_method: {
          ...getPaymentParams(currentUser, formValues).payment_method_data,
          card,
        },
      },
    }).then();
    setIsSubmitting(false);
  };
  const handleSubmitDefaultPaymentMethod = () => {
    setIsSubmitting(true);
    onSubmit({
      stripe,
      paymentParams: {
        payment_method: ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.stripePaymentMethodId,
      },
    }).then();
    setIsSubmitting(false);
  };
  const handleRemovePaymentMethod = () => {
    onDeletePaymentMethod().then(() => {
      fetchStripeCustomer();
    });
  };
  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUserLoaded
    ? `${ensuredCurrentUser.attributes.profile.firstName} ${ensuredCurrentUser.attributes.profile.lastName}`
    : null;
  const countryValue = getCountryValue(config.locale);
  const initalValuesForStripePayment = { name: userName, country: countryValue };
  const card = hasDefaultPaymentMethod
    ? ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.card
    : null;
  const showForm =
    cardState === 'replaceCard' || !hasDefaultPaymentMethod || !hasUsablePaymentMethod || requiresCardResave;
  const showCardDetails = !!hasDefaultPaymentMethod && hasUsablePaymentMethod && !requiresCardResave;

  return (
    <div>
      {!isLoading && showCardDetails ? (
        <SavedCardDetails
          card={card}
          onManageDisableScrolling={onManageDisableScrolling}
          onChange={setCardState}
          onDeleteCard={handleRemovePaymentMethod}
          deletePaymentMethodInProgress={deletePaymentMethodInProgress || isLoading}
        />
      ) : null}
      {showForm ? (
        <PaymentMethodsForm
          className={css.paymentForm}
          formId="PaymentMethodsForm"
          initialValues={initalValuesForStripePayment}
          onSubmit={handleSubmitCard}
          handleRemovePaymentMethod={handleRemovePaymentMethod}
          hasDefaultPaymentMethod={hasDefaultPaymentMethod}
          addPaymentMethodError={addPaymentMethodError}
          deletePaymentMethodError={deletePaymentMethodError}
          createStripeCustomerError={createStripeCustomerError}
          handleCardSetupError={handleCardSetupError}
          inProgress={isSubmitting || isLoading}
        />
      ) : null}
      <Divider className={css.divider}/>
      {!showForm && (
        <div className={css.submitContainer}>
          {/*{hasPaymentErrors ? (*/}
          {/*  <span className={css.errorMessage}>{paymentErrorMessage}</span>*/}
          {/*) : null}*/}
          <PrimaryButton
            className={css.submitButton}
            onClick={handleSubmitDefaultPaymentMethod}
            inProgress={isLoading}
            // disabled={submitDisabled}
          >
            <FormattedMessage id="StripePaymentForm.submitConfirmPaymentInfo"/>
          </PrimaryButton>
        </div>
      )}
      {confirmManualPaymentMultiBookingError && (
        <span className={css.errorMessage}>{paymentErrorMessage}</span>
      )}
    </div>
  );
};

PaymentFormWithPaymentIntent.defaultProps = {
  currentUser: null,
  addPaymentMethodError: null,
  deletePaymentMethodError: null,
  createStripeCustomerError: null,
  handleCardSetupError: null,
};

PaymentFormWithPaymentIntent.propTypes = {
  currentUser: propTypes.currentUser,
  addPaymentMethodError: object,
  deletePaymentMethodError: object,
  createStripeCustomerError: object,
  handleCardSetupError: object,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onDeletePaymentMethod: func.isRequired,
  fetchStripeCustomer: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = state.paymentMethods;
  const {
    isConfirmManualPaymentMultiBookingLoading: isLoading,
    confirmManualPaymentMultiBookingError,
  } = state.transaction;
  const { stripeCustomerFetched, stripePaymentMethodDetails } = state.PaymentMethodsTab;
  const { handleCardSetupError } = state.stripe;
  return {
    isLoading,
    currentUser,
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    confirmManualPaymentMultiBookingError,
    stripeCustomerFetched,
    stripePaymentMethodDetails,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onSavePaymentMethod: (stripeCustomer, newPaymentMethod, stripeCustomerEmail) =>
    dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod, stripeCustomerEmail)),
  onDeletePaymentMethod: params => dispatch(deletePaymentMethod(params)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  injectIntl,
)(PaymentFormWithPaymentIntent);
